import React, { useMemo, useState } from "react";
import { UserIcon, UserGroupIcon } from "@heroicons/react/24/solid";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

import EtUserActionPerformanceSelfChart from "@/components/actions/EtUserActionPerformanceSelfChart";
import EtUserActionPerformanceCollectiveTimeSeriesChart from "@/components/actions/EtUserActionPerformanceCollectiveTimeSeriesChart";

const EtTrinityActionsChartDemo = () => {
  // Author - Atul Pandey 
  // First Created - 27 Mar 2026
  // Purpose - demo visual for Begin Here > Actions > Visualize
  // Uses the actual performance components with sample onboarding data

  const [activeMainTab, setActiveMainTab] = useState("Action Performance");
  const [activeFocusTab, setActiveFocusTab] = useState("self");
  const [activeCollectiveTab, setActiveCollectiveTab] = useState("chart");

  // --------------------------------------------------
  // Sample root action for self chart + collective target
  // --------------------------------------------------
  const rootAction = useMemo(
    () => ({
      _id: "action-root-1",
      userId: {
        _id: "user-1",
        name: "Ava Sharma",
        username: "ava",
      },
      userActionName: "Open 15 strategic client conversations",
      actionGoalRole: "Outcome",
      actionRecurrencePattern: "OneTime",
      targetValue: 15,
      actualValue: 8,
      actionUoM: "conversations",
      userActionNotes: [
        {
          _id: "note-1",
          actionNoteDate: "2026-03-03T09:00:00.000Z",
          actionNoteActualValue: 2,
        },
        {
          _id: "note-2",
          actionNoteDate: "2026-03-10T10:00:00.000Z",
            actionNoteActualValue: 4,
        },
        {
          _id: "note-3",
          actionNoteDate: "2026-03-12T15:00:00.000Z",
            actionNoteActualValue: 5,
        },
        {
          _id: "note-4",
          actionNoteDate: "2026-03-18T15:00:00.000Z",
            actionNoteActualValue: 7,
        },
        {
          _id: "note-5",
          actionNoteDate: "2026-03-20T15:00:00.000Z",
            actionNoteActualValue: 8,
        },
      ],
    }),
    []
  );

  // --------------------------------------------------
  // Sample collective actions for collective timeseries
  // --------------------------------------------------
  const collectiveActions = useMemo(
    () => [
      {
        _id: "action-user-1",
        userId: {
          _id: "user-1",
          name: "Ava Sharma",
          username: "ava",
        },
        userActionNotes: [
          {
            _id: "u1n1",
            actionNoteDate: "2026-03-03T09:00:00.000Z",
            actionNoteActualValue: 2,
          },
          {
            _id: "u1n2",
            actionNoteDate: "2026-03-10T10:00:00.000Z",
            actionNoteActualValue: 4,
          },
          {
            _id: "u1n3",
            actionNoteDate: "2026-03-12T15:00:00.000Z",
            actionNoteActualValue: 5,
          },
          {
            _id: "u1n3",
            actionNoteDate: "2026-03-18T15:00:00.000Z",
            actionNoteActualValue: 7,
          },
          {
            _id: "u1n3",
            actionNoteDate: "2026-03-20T15:00:00.000Z",
            actionNoteActualValue: 8,
          },
        ],
      },
      {
        _id: "action-user-2",
        userId: {
          _id: "user-2",
          name: "Rohan Mehta",
          username: "rohan",
        },
        userActionNotes: [
          {
            _id: "u2n1",
            actionNoteDate: "2026-03-05T13:00:00.000Z",
            actionNoteActualValue: 1,
          },
          {
            _id: "u2n2",
            actionNoteDate: "2026-03-12T09:30:00.000Z",
            actionNoteActualValue: 3,
          },
          {
            _id: "u2n3",
            actionNoteDate: "2026-03-20T14:00:00.000Z",
            actionNoteActualValue: 4,
          },
        ],
      },
      {
        _id: "action-user-3",
        userId: {
          _id: "user-3",
          name: "Maya Chen",
          username: "maya",
        },
        userActionNotes: [
          {
            _id: "u3n1",
            actionNoteDate: "2026-03-07T08:30:00.000Z",
            actionNoteActualValue: 1,
          },
          {
            _id: "u3n2",
            actionNoteDate: "2026-03-15T11:00:00.000Z",
            actionNoteActualValue: 2,
          },
          {
            _id: "u3n3",
            actionNoteDate: "2026-03-22T16:30:00.000Z",
            actionNoteActualValue: 3,
          },
        ],
      },
    ],
    []
  );

  // --------------------------------------------------
  // Sample snapshot for contribution cards
  // --------------------------------------------------
  const performanceSnapshot = useMemo(
    () => ({
      achievedValue: 15,
      targetValue: 15,
      progressPercent: 100,
      uom: "conversations",
      contributors: [
        {
          userId: "user-1",
          userName: "Ava Sharma",
          username: "ava",
          actualValue: 8,
          percentOfTarget: 53,
        },
        {
          userId: "user-2",
          userName: "Rohan Mehta",
          username: "rohan",
          actualValue: 4,
          percentOfTarget: 27,
        },
        {
          userId: "user-3",
          userName: "Maya Chen",
          username: "maya",
          actualValue: 3,
          percentOfTarget: 20,
        },
      ],
    }),
    []
  );


  const sampleSelfProgressNotes = useMemo(
  () => [
    {
      _id: "spn-1",
      actionNoteDate: "2026-03-19T10:15:00.000Z",
      actionNoteText: "Reached out to the first set of target stakeholders and got initial traction.",
      actionNoteProgress: 25,
    },
    {
      _id: "spn-2",
      actionNoteDate: "2026-03-21T15:30:00.000Z",
      actionNoteText: "Moved two conversations forward and clarified next steps.",
      actionNoteProgress: 50,
    },
    {
      _id: "spn-3",
      actionNoteDate: "2026-03-24T11:00:00.000Z",
      actionNoteText: "Closed alignment with the internal team and improved the outreach approach.",
      actionNoteProgress: 75,
    },
    {
      _id: "spn-4",
      actionNoteDate: "2026-03-26T16:45:00.000Z",
      actionNoteText: "Completed the intended set of strategic conversations for this cycle.",
      actionNoteProgress: 100,
    },
  ],
  []
);

const getFirstName = (name = "") => name.split(" ")[0];

  return (
    <div className="mt-3">
      {/* top summary */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
        {/* <p className="text-xs font-bold text-orange-700">Sample Action Performance</p> */}
        <p className="text-xs font-bold text-orange-700">Sample Action Impact</p>
        <p className="text-sm text-gray-700 mt-1">
          {/* See how one action can show your own progress and, when relevant, the collective contribution toward the shared target. */}
          See how each action can demonstrate your contribution as you progress and, when relevant, the collective contribution toward the shared target.
        </p>
      </div>

      <div className="mt-3 flex justify-start space-x-3 border-b border-gray-200 pb-2">
        {["Action Progress", "Action Performance"].map((tab) => (
            <button
            key={tab}
            onClick={() => setActiveMainTab(tab)}
            className={`px-3 py-1.5 rounded-full border text-sm font-semibold ${
                activeMainTab === tab
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white text-gray-600 border-gray-200"
            }`}
            >
            {tab}
            </button>
        ))}
        </div>

        {activeMainTab === "Action Progress" && (
        <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between gap-3">
            <div>
                <p className="text-sm font-bold text-gray-800">Action Progress Trail</p>
                <p className="text-xs text-gray-500 mt-1">
                Sample self progress updates for the action.
                </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                <p className="text-xs font-semibold text-gray-700">Self</p>
            </div>
            </div>

            <div className="bg-gray-100 rounded mt-3 p-2">
            {sampleSelfProgressNotes
                .slice()
                .sort((a, b) => new Date(b.actionNoteDate) - new Date(a.actionNoteDate))
                .map((note, index) => (
                <div
                    key={note._id + index}
                    className="bg-white border border-gray-200 rounded p-2 my-2"
                >
                    <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-12 md:col-span-6 flex items-start">
                        <div>
                        <p className="font-bold text-[10px] mx-1 py-[1px]">
                            {index + 1}.
                        </p>
                        </div>

                        <div>
                        <p className="text-[10px] mx-1 rounded-full px-2 py-[1px] bg-lime-100 text-lime-800">
                            {new Date(note.actionNoteDate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            })}
                        </p>
                        </div>

                        <div>
                        <p className="text-[11px] text-gray-700 mx-1">
                            {note.actionNoteText}
                        </p>
                        </div>
                    </div>

                    <div className="col-span-8 md:col-span-3">
                        <div className="rounded-full bg-gray-200 relative h-[10px]">
                        <div
                            className="rounded-full bg-[#552a9a] h-[10px]"
                            style={{
                            width: `${note?.actionNoteProgress || 0}%`,
                            }}
                        ></div>
                        <p
                            className={`text-[8px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
                            note?.actionNoteProgress < 45 ? "text-black" : "text-white"
                            }`}
                        >
                            {note?.actionNoteProgress}%
                        </p>
                        </div>
                    </div>

                    <div className="col-span-4 md:col-span-3">
                        <p className="text-[10px] text-gray-400 text-right">
                        {new Date(note.actionNoteDate).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                        })}
                        </p>
                    </div>
                    </div>
                </div>
                ))}
            </div>
        </div>
        )}

        {activeMainTab === "Action Performance" && (
        <>
            {/* focus tabs */}
            <div className="mt-3 flex justify-start space-x-3 border-b border-gray-200 pb-2">
            {[
                { key: "self", label: "Self", Icon: UserIcon },
                { key: "collective", label: "Collective", Icon: UserGroupIcon },
            ].map(({ key, label, Icon }) => (
                <button
                key={key}
                onClick={() => setActiveFocusTab(key)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${
                    activeFocusTab === key
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
                >
                <Icon className="h-4 w-4" />
                {label}
                </button>
            ))}
            </div>

            {/* self */}
            {activeFocusTab === "self" && (
            <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-sm font-bold text-gray-800">{rootAction.userActionName}</p>
                    <p className="text-xs text-gray-500 mt-1">
                    Your achieved value so far:{" "}
                    <span className="font-bold text-orange-700">
                        {rootAction.actualValue} / {rootAction.targetValue} {rootAction.actionUoM}
                    </span>
                    </p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                    <p className="text-xs font-semibold text-gray-700">
                    Target Progress: {Math.round((rootAction.actualValue / rootAction.targetValue) * 100)}%
                    </p>
                </div>
                </div>

                <EtUserActionPerformanceSelfChart action={rootAction} />
            </div>
            )}

            {/* collective */}
            {activeFocusTab === "collective" && (
            <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
                {/* collective summary */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="grid grid-cols-3 gap-3">
                    <div>
                    <p className="text-[11px] font-bold text-gray-700">Achieved</p>
                    <p className="text-sm text-gray-800">
                        {performanceSnapshot.achievedValue} {performanceSnapshot.uom}
                    </p>
                    </div>
                    <div>
                    <p className="text-[11px] font-bold text-gray-700">Target</p>
                    <p className="text-sm text-gray-800">
                        {performanceSnapshot.targetValue} {performanceSnapshot.uom}
                    </p>
                    </div>
                    <div>
                    <p className="text-[11px] font-bold text-gray-700">Performance</p>
                    <p className="text-sm text-gray-800">{performanceSnapshot.progressPercent}%</p>
                    </div>
                </div>
                </div>

                {/* sub tabs */}
                <div className="mt-3 flex space-x-3 border-b border-gray-200 pb-2">
                {[
                    { key: "chart", label: "Performance Chart" },
                    { key: "contributions", label: "Performance Contributions" },
                ].map((tab) => (
                    <button
                    key={tab.key}
                    onClick={() => setActiveCollectiveTab(tab.key)}
                    className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${
                        activeCollectiveTab === tab.key
                        ? "bg-orange-600 text-white border-orange-600"
                        : "bg-white text-gray-600 border-gray-200"
                    }`}
                    >
                    {tab.label}
                    </button>
                ))}
                </div>

                {activeCollectiveTab === "chart" && (
                <div className="mt-3">
                    <EtUserActionPerformanceCollectiveTimeSeriesChart
                    rootAction={rootAction}
                    collectiveActions={collectiveActions}
                    loggedInUserId="user-1"
                    />
                </div>
                )}

                {activeCollectiveTab === "contributions" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    {performanceSnapshot.contributors.map((c, idx) => {
                    const isMe = c.userId === "user-1";

                    return (
                        <div
                        key={`${c.userId}-${idx}`}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center"
                        >
                        <div className="h-42">
                            <CircularProgressbarWithChildren
                            value={c.percentOfTarget}
                            circleRatio={0.75}
                            styles={{
                                path: {
                                stroke: "#f97316",
                                transform: "rotate(-135deg)",
                                transformOrigin: "center center",
                                },
                                trail: {
                                stroke: "#e5e7eb",
                                transform: "rotate(-135deg)",
                                transformOrigin: "center center",
                                },
                            }}
                            >
                            <div className="h-8 w-8 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center">
                                {getFirstName(c.userName)?.[0]}
                            </div>

                            <p className="text-[10px] font-semibold mt-1">
                                {getFirstName(c.userName)}
                                {isMe && (
                                <span className="ml-1 text-[9px] text-orange-600 font-bold">
                                    (Me)
                                </span>
                                )}
                            </p>

                            <p className="text-xs font-bold text-gray-700">
                                {c.percentOfTarget}%
                            </p>

                            <p className="text-[10px] text-gray-500">
                                {c.actualValue} {performanceSnapshot.uom}
                            </p>
                            </CircularProgressbarWithChildren>
                        </div>
                        </div>
                    );
                    })}
                </div>
                )}
            </div>
            )}
        </>
        )}
    </div>
  );
};

export default EtTrinityActionsChartDemo;